import { useEffect, useState } from "react";
import type { NowPlaying, RecentlyPlayedTrack, RequestsClosedReason, UpNextTrack } from "@touchscreen/api-client";
import { computeTrackProgress, type TrackProgress } from "@touchscreen/live-state";
import { useTouchscreenContext } from "./TouchscreenProvider.js";
import { useVenueStateQuery } from "./useVenueStateQuery.js";

export interface VenueState {
  ready: boolean;
  error: unknown | null;
  nowPlaying: NowPlaying | null;
  /** `null` while `nowPlaying` is `null` — nothing to interpolate. */
  progress: TrackProgress | null;
  upNext: UpNextTrack[];
  recentlyPlayed: RecentlyPlayedTrack[];
  requestsOpen: boolean;
  requestsClosedReason: RequestsClosedReason | null;
  nextTransitionAt: string | null;
  /** Staff kill-switch — browse stays; Request CTAs should hide. Prefer live `/state`. */
  requestsDisabled: boolean;
  playerOnline: boolean;
}

/**
 * The shared shape both surfaces render from. `progress` re-derives every second locally, against
 * the server clock offset — this hook never polls on its own; `useVenueStateQuery` owns that.
 */
export function useVenueState(): VenueState {
  const { client, boot, venue } = useTouchscreenContext();
  const { data: snapshot } = useVenueStateQuery();
  const [tick, setTick] = useState(0);

  const nowPlaying = snapshot?.now_playing ?? null;

  useEffect(() => {
    if (!nowPlaying) return;
    // A 1s local tick for progress-bar smoothness. Not a polling schedule — see live-state.
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
    // `tick` intentionally excluded — this effect only needs to restart when the track changes.
  }, [nowPlaying?.sm_id, nowPlaying?.started_at]);

  const progress = nowPlaying ? computeTrackProgress(nowPlaying, client.getClockOffset()) : null;
  // Referencing `tick` here (even unused otherwise) is what makes the above interval matter —
  // it forces this hook to re-render every second while a track is playing.
  void tick;

  const sessionDisabled = venue?.requests.disabled ?? false;

  return {
    ready: boot.status === "ready",
    // A poll error never blanks an already-showing screen — `useVenueStateQuery` backs off on
    // its own; only a boot (session) failure is a real error worth surfacing to the app shell.
    error: boot.status === "error" ? boot.error : null,
    nowPlaying,
    progress,
    upNext: snapshot?.up_next ?? [],
    recentlyPlayed: snapshot?.recently_played ?? [],
    requestsOpen: snapshot?.requests.open ?? false,
    requestsClosedReason: snapshot?.requests.reason ?? null,
    nextTransitionAt: snapshot?.requests.next_transition_at ?? null,
    requestsDisabled: snapshot?.requests.disabled ?? sessionDisabled,
    playerOnline: snapshot?.player.online ?? false,
  };
}
